"use client";
import React, {useState, useEffect, useContext} from "react";
import {Calendar, momentLocalizer} from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {Dialog, DialogContent, DialogPortal, DialogOverlay, DialogClose, DialogTitle} from "@repo/ui/Dialog";
import {createClient} from "@repo/shared/supabase/client";
import Button from "@repo/ui/Button";
import GoogleCalendarLogin from "@repo/ui/GoogleCalendarLogin";
import {usePathname} from "next/navigation";
import UserContext from "@repo/ui/UserContext";
import {toast} from "react-hot-toast";
import {Time} from "@internationalized/date";
import {
    Calendar as CalendarIcon,
    MapPin as MapPinIcon,
    User as UserIcon,
    Clock as ClockIcon,
    Building as BuildingIcon,
    FileText as DocumentTextIcon,
    Plus as PlusIcon,
    Tag as TagIcon,
    Link as LinkIcon
} from "lucide-react";

const localizer = momentLocalizer(moment);
// Event types for different contexts
const EVENT_TYPES: any = {
    quotation: {
        label: "Preventivo",
        color: "#10b981",
        bgColor: "#ecfdf5",
        icon: DocumentTextIcon
    },
    contract: {
        label: "Commessa",
        color: "#3b82f6",
        bgColor: "#eff6ff",
        icon: BuildingIcon
    },
    inspection: {
        label: "Sopralluogo",
        color: "#f59e0b",
        bgColor: "#fef3c7",
        icon: MapPinIcon
    },
    assembly: {
        label: "Montaggio",
        color: "#8b5cf6",
        bgColor: "#f3e8ff",
        icon: PlusIcon
    },
    general: {
        label: "Generale",
        color: "#6366f1",
        bgColor: "#eef2ff",
        icon: CalendarIcon
    }
};

const InternalCalendar = ({setValue, getValues, reset,setEventsCallback}: { setValue: any, getValues: any, reset: any,setEventsCallback?:any}) => {
    let {user, user_internal} = useContext(UserContext);
    const user_id = user.id
    const supabase = createClient();
    const [events, setEvents] = useState<any>([]);
    const [linkedEvents, setLinkedEvents] = useState<any>([]);
    const [view, setView] = useState<any>("month");
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [newEvent, setNewEvent] = useState<any>(null);
    const [contextData, setContextData] = useState<any>(null);
    const [context_id, setContext_id] = useState<any>(null);
    const [context_type, setContext_type] = useState<any>(null);
    const [needsSetup, setNeedsSetup] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>({
        title: "",
        location: "",
        description: "",
        attendees: [],
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        // New building industry specific fields
        client_name: "",
        contract_number: "",
        quotation_id: "",
        additional_notes: "",
        event_type: "general",
        document_links: ""
    });

    const pathname = usePathname();

    // Determine context from the URL path if not provided explicitly
    useEffect(() => {
        if (!context_type) {
            // Extract context type from pathname
            const pathParts = pathname.split('/');
            let detectedType = "general";

            // Simple path detection logic - enhance based on your actual routing structure
            if (pathname.includes('/quotations')) detectedType = "quotation";
            else if (pathname.includes('/contracts')) detectedType = "contract";
            else if (pathname.includes('/inspections')) detectedType = "inspection";
            else if (pathname.includes('/assemblies')) detectedType = "assembly";
            else if (pathname.includes('/interferences')) detectedType = "interference";

            // Extract ID if available in the URL
            let detectedId = null;
            if (pathParts.length > 2 && !isNaN(parseInt(pathParts[pathParts.length - 1]))) {
                detectedId = pathParts[pathParts.length - 1];
            }

            console.log(`Detected context: ${detectedType}, ID: ${detectedId}`);
            fetchContextData(detectedType, detectedId);
            setContext_type(detectedType)
            setContext_id(detectedId)
        } else {
            fetchContextData(context_type, context_id);
        }
    }, [pathname, context_type, context_id]);

    // Fetch context-specific data from Supabase based on the current page/context
    const fetchContextData = async (type: any, id: any) => {
        if (!type || type === "general") return;

        let data: any = null;

        try {
            // Query different tables based on context type
            switch (type) {
                case "quotation":
                    const {data: quotation} = await supabase
                        .from('quotations')
                        .select('*')
                        .eq('id', id)
                        .single();
                    data = quotation;
                    break;
                case "contract":
                    const {data: contract} = await supabase
                        .from('contracts')
                        .select('*')
                        .eq('id', id)
                        .single();
                    data = contract;
                    break;
                case "interference":
                    const {data: interference} = await supabase
                        .from('interferences')
                        .select('*')
                        .eq('id', id)
                        .single();
                    data = interference;
                    break;
                case "inspection":
                    const {data: inspection, error} = await supabase
                        .from('inspections')
                        .select('*, quotations(*)')
                        .eq('id', id)
                        .single();
                    console.log(error)
                    data = inspection;
                    break;
                case "assembly":
                    const {data: assembly} = await supabase
                        .from('assemblies')
                        .select('*, contracts(*,quotations_complete(*))')
                        .eq('id', id)
                        .single();
                    data = assembly;
                    break;
            }

            console.log(`Fetched ${type} data:`, data);
            setContextData(data);

            // Pre-fill form with context data
            console.log(data)
            if (data) {
                const quotation = (data.quotations_complete || data.quotations || data.contracts.quotations_complete)
                const client_name = `${quotation?.lead?.name} ${quotation?.lead?.surname} ${quotation?.lead?.email}` || data.client_name || ''
                setFormData((prevData: any) => ({
                    ...prevData,
                    attendees: quotation?.lead?.email?.length > 0 ? [quotation?.lead?.email] : [],
                    title: `${EVENT_TYPES[type].label}: ${data.title || data.name || client_name}`,
                    location: data.location || data.address || '',
                    client_name: `${quotation?.lead?.name} ${quotation?.lead?.surname} ${quotation?.lead?.email}` || data.client_name || '',
                    contract_number: data.contracts?.number || data.id || '',
                    quotation_id: quotation?.number || data.quotation_id || '',
                    event_type: type,
                    document_links: `${process.env.NEXT_PUBLIC_BASE_URL}/quotations/fast/public/${quotation.id}` || ''
                }));
            }
        } catch (error) {
            console.error(`Error fetching ${type} data:`, error);
        }
    };

    const fetchGoogleCalendarEvents = async (userId: string) => {
        const url = `/api/calendar/google/events?user_id=${userId}`;
        console.log("Fetching Google Calendar events for user:", userId, url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'User-Agent': '*',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch events:", response.statusText);
            return {events: [], error: response.statusText};
        }

        const data = await response.json();
        setEventsCallback(data.events)
        return {events: data.events, error: null};
    };

    // Fetch events from Supabase that are linked to Google Calendar
    const fetchLinkedEvents = async () => {
        const {data, error} = await supabase
            .from('calendar_events')
            .select('*')
            .eq('user_id', user_id);

        if (error) {
            console.error("Error fetching linked events:", error);
            return [];
        }

        setLinkedEvents(data)

        return data || [];
    };

    useEffect(() => {
        supabase
            .from('user_tokens')
            .select('id, access_token, refresh_token')
            .eq('user_id', user_id)
            .order('id', {ascending: false}).then(({data: tokens, error}) => {
            if (!tokens || tokens.length === 0 || error) {
                console.error("No tokens found for user:", user_id);
                setNeedsSetup(true)
                return;
            }
            Promise.all([
                fetchGoogleCalendarEvents(user_id),
                fetchLinkedEvents()
            ]).then(([{events: googleEvents, error}, linkedEvents]: any) => {
                console.log('Raw events from API:', googleEvents);
                console.log('Linked events from Supabase:', linkedEvents);

                if (error || !googleEvents) {
                    console.error("Error fetching Google events:", error);
                    return;
                }

                // Create a map of Google event IDs to our additional metadata
                const eventMetadataMap = linkedEvents.reduce((acc: any, event: any) => {
                    acc[event.google_event_id] = event;
                    return acc;
                }, {});

                const mappedEvents = googleEvents.map((event: any) => {
                    // Convert string dates to Date objects
                    const startDate = new Date(event.start);
                    const endDate = new Date(event.end);

                    // Get additional metadata if available
                    const metadata = eventMetadataMap[event.id] || {};
                    const eventType = metadata.event_type || "general";

                    return {
                        title: event.title || "Untitled",
                        start: startDate,
                        end: endDate,
                        location: event.location || "No location",
                        description: event.description || "No description",
                        attendees: Array.isArray(event.attendees) ? event.attendees : [],
                        id: event.id,
                        // Use event type specific color or the calendar color
                        calendarColor: event.calendarColor || `${event.googleCalendarColor}` || "#3174ad",
                        calendarName: event.calendarName || `${event.googleCalendarName} | FinestraPro ${event.appCalendarType}` || "Generico",
                        owner: event.owner || "",
                        allDay: false,
                        // Include our additional metadata
                        client_name: metadata.client_name || "",
                        contract_number: metadata.contract_number || "",
                        quotation_id: metadata.quotation_id || "",
                        additional_notes: metadata.additional_notes || "",
                        event_type: eventType,
                        document_links: metadata.document_links || "",
                        metadata_id: metadata.id  // Store the ID of our metadata record
                    };
                });

                console.log('Mapped events for calendar:', mappedEvents);
                setEvents(mappedEvents);
            }).catch(err => console.error("Fetch error:", err));
        })
    }, [user_id]);

    useEffect(() => {
        if (selectedEvent) {
            setFormData({
                title: selectedEvent.title || "",
                location: selectedEvent.location || "",
                description: selectedEvent.description || "",
                attendees: Array.isArray(selectedEvent.attendees) ? selectedEvent.attendees.join(", ") : "",
                startDate: moment(selectedEvent.start).format("YYYY-MM-DD"),
                startTime: moment(selectedEvent.start).format("HH:mm"),
                endDate: moment(selectedEvent.end).format("YYYY-MM-DD"),
                endTime: moment(selectedEvent.end).format("HH:mm"),
                // Include our additional fields
                client_name: selectedEvent.client_name || "",
                contract_number: selectedEvent.contract_number || "",
                quotation_id: selectedEvent.quotation_id || "",
                additional_notes: selectedEvent.additional_notes || "",
                event_type: selectedEvent.event_type || "general",
                document_links: selectedEvent.document_links || ""
            });
        } else if (newEvent) {
            // For new events, pre-fill with context data if available
            const defaultTitle = contextData ?
                `${EVENT_TYPES[formData.event_type].label}: ${contextData.title || contextData.name || ''}` :
                "";

            setFormData((prev: any) => ({
                ...prev,
                startDate: moment(newEvent).format("YYYY-MM-DD"),
                startTime: moment(newEvent).format("HH:mm"),
                endDate: moment(newEvent).format("YYYY-MM-DD"),
                endTime: moment(newEvent).add(1, 'hours').format("HH:mm"),
                title: prev.title || defaultTitle
            }));
        }
    }, [selectedEvent, newEvent, contextData]);

    async function saveEvent() {
        const {
            data: {user},
        } = await supabase.auth.getUser();

        if (!user) {
            console.error("User not authenticated");
            return;
        }

        // Prepare the attendees list
        console.log(formData.attendees)
        const attendeesList = Array.isArray(formData.attendees) ? formData.attendees : (formData.attendees && formData.attendees.length > 0 ?
            formData.attendees?.split(",").map((attendee: any) => attendee.trim()) :
            []);

        // Create the start and end dates from form data
        const startDateTime = moment(`${formData.startDate} ${formData.startTime}`, "YYYY-MM-DD HH:mm").toDate();
        const endDateTime = moment(`${formData.endDate} ${formData.endTime}`, "YYYY-MM-DD HH:mm").toDate();

        // Prepare Google Calendar event payload
        const googleEventPayload = {
            title: formData.title,
            location: formData.location,
            description: `${formData.description}\n\n---\nClient: ${formData.client_name}\nContract #: ${formData.contract_number}\nQuotation ID: ${formData.quotation_id}\n\nAdditional Notes: ${formData.additional_notes}\n\nDocument Links: ${formData.document_links}`,
            attendees: attendeesList,
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString(),
            eventId: selectedEvent?.id || null,
            userId: user.id
        };

        console.log("Saving event to Google Calendar:", googleEventPayload);

        try {
            // Save/update the event in Google Calendar
            const res = await fetch("/api/calendar/google/events/event", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(googleEventPayload),
            });

            const data = await res.json();

            if (!data.success) {
                toast.error(data.error || "Failed to save event to Google Calendar");
            } else {
                toast.success('Evento salvato')
            }

            const googleEvent = data.event;
            console.log("Google Calendar event created/updated:", googleEvent);

            // Now save metadata to Supabase
            const metadataPayload = {
                user_id: user.id,
                google_event_id: googleEvent.id,
                client_name: formData.client_name,
                contract_number: formData.contract_number,
                quotation_id: formData.quotation_id,
                additional_notes: formData.additional_notes,
                event_type: formData.event_type,
                document_links: formData.document_links,
                context_type: context_type || formData.event_type,
                context_id: context_id || null
            };

            // Update or insert the metadata record
            let metadataResult;
            if (selectedEvent?.metadata_id) {
                // Update existing record
                const {data, error} = await supabase
                    .from('calendar_events')
                    .update(metadataPayload)
                    .eq('id', selectedEvent.metadata_id)
                    .select()
                    .single();

                if (error) throw error;
                metadataResult = data;
            } else {
                // Insert new record
                const {data, error} = await supabase
                    .from('calendar_events')
                    .insert(metadataPayload)
                    .select()
                    .single();

                if (error) throw error;
                metadataResult = data;
            }

            console.log("Saved event metadata to Supabase:", metadataResult);
            const hours = startDateTime.getHours()
            const minutes = startDateTime.getMinutes()
            const updateData = {
                confirmed: true,
                programmed: true,
                status: 'Programmato',
                programmed_date: startDateTime,
                date: startDateTime.toISOString().split('T')[0],
                time: new Time(hours ?? 0, minutes ?? 0),
            }
            // NEW CODE: Update the source context table
            const contextUpdateResult = await updateContextTable(
                formData.event_type,
                context_id || metadataResult.context_id,
                updateData
            );

            console.log("Updated context table:", contextUpdateResult);

            const newEventData = {
                ...googleEvent,
                // client_name: formData.client_name,
                // contract_number: formData.contract_number,
                // quotation_id: formData.quotation_id,
                // additional_notes: formData.additional_notes,
                // event_type: formData.event_type,
                // document_links: formData.document_links,
                // calendarColor: EVENT_TYPES[formData.event_type]?.color || googleEvent.calendarColor,
                // metadata_id: metadataResult.id
            };

            setEvents((prev: any) => {
                if (selectedEvent) {
                    return prev.map((evt: any) => (evt.id === selectedEvent.id ? newEventData : evt));
                }
                return [...prev, newEventData];
            });

            // Reset state
            setNewEvent(null);
            setSelectedEvent(null);

        } catch (error: any) {
            console.error("Error saving event:", error);
            alert("Failed to save event: " + error.message);
        }
    }

// New function to update the context table based on event type
    async function updateContextTable(eventType: any, contextId: any, updateData: any) {
        if (!eventType || !contextId) {
            console.log("No context to update");
            return null;
        }

        let tableName;
        switch (eventType) {
            case "quotation":
                tableName = "quotations";
                break;
            case "contract":
                tableName = "contracts";
                break;
            case "inspection":
                tableName = "inspections";
                break;
            case "assembly":
                tableName = "assemblies";
                break;
            default:
                console.log(`No table mapping for event type: ${eventType}`);
                return null;
        }

        try {

            console.log("updateData", updateData);
            reset((prev: any) => ({...prev, ...updateData}));

            return {success: true, data: getValues()};
        } catch (error: any) {
            console.error(`Exception updating ${tableName}:`, error);
            return {success: false, error: error.message};
        }
    }

    const generateTimeOptions = () => {
        const times = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 15) {
                const hour = h < 10 ? `0${h}` : `${h}`;
                const minute = m < 10 ? `0${m}` : `${m}`;
                times.push(`${hour}:${minute}`);
            }
        }
        return times;
    };

    const eventStyleGetter = (event: any, start: any, end: any, isSelected: any) => {
        const baseColor = event.calendarColor || EVENT_TYPES[event.event_type]?.color || "#3174ad";

        let style: any = {
            backgroundColor: baseColor,
            borderRadius: '0px',
            opacity: 0.8,
            color: 'white',
            border: '0px',
            display: 'block',
            fontWeight: 'bold'
        };

        // Add a left border with different color based on event type
        if (event.event_type && EVENT_TYPES[event.event_type]) {
            style.borderLeft = `5px solid ${EVENT_TYPES[event.event_type].color}`;
        }

        // If selected, highlight it
        if (isSelected) {
            style.opacity = 1;
            style.boxShadow = '0 0 0 2px #000';
        }

        return {style};
    };

    // Custom event component to show more details
    const EventComponent = ({ event }:any) => {
        const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.general;
        const IconComponent = eventType.icon;

        return (
            <div className="flex items-center gap-1 text-white">
                <IconComponent className="w-3 h-3 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs truncate">{event.calendarName}</div>
                    <div className="font-semibold text-xs truncate">{event.title}</div>
                    {event.client_name && (
                        <div className="text-xs opacity-90 truncate">{event.client_name}</div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 bg-white text-gray-900 rounded-2xl shadow-lg">
            {needsSetup ? <>Collega il tuo account <GoogleCalendarLogin/></> : <>
                {/*<UpcomingEvents context_type={context_type} events={events} formData={formData} linkedEvents={linkedEvents} setSelectedEvent={setSelectedEvent}/>*/}
                <div className="rounded-lg overflow-hidden border border-gray-300 bg-gray-100 shadow p-4">
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        allDayAccessor="allDay"
                        view={view}
                        onView={(newView: any) => setView(newView)}
                        eventPropGetter={eventStyleGetter}
                        components={{
                            event: EventComponent
                        }}
                        onSelectEvent={(event: any) => {
                            console.log("Selected event:", event);
                            setSelectedEvent(event);
                        }}
                        onSelectSlot={(slotInfo: any) => {
                            console.log("Selected slot:", slotInfo);
                            setNewEvent(slotInfo.start);
                        }}
                        selectable
                        style={{
                            height: 1000,
                            color: "#333",
                            background: "#fff",
                            borderRadius: "12px",
                            padding: "10px",
                        }}
                    />
                </div>
            </>}
            {/* Radix Dialog for Event Editing / Creating */}
            {(selectedEvent || newEvent) && (
                <Dialog open={!!selectedEvent || !!newEvent}
                        onOpenChange={(open) => !open && (setSelectedEvent(null), setNewEvent(null))}>
                    <DialogPortal>
                        <DialogOverlay className="fixed inset-0 bg-black bg-opacity-50"/>
                        <DialogContent
                            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
                            <DialogTitle
                                className="text-xl font-bold">{selectedEvent ? "Edit Event" : "Add New Event"}</DialogTitle>

                            {/* Event Type */}
                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Event Type</label>
                                <select
                                    value={formData.event_type}
                                    onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                >
                                    {Object.entries(EVENT_TYPES).map(([value, {label}]: any) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Basic Event Info */}
                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Title</label>
                                <input
                                    type="text"
                                    placeholder="Event Title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Location</label>
                                <input
                                    type="text"
                                    placeholder="Event Location"
                                    value={formData.location}
                                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            {/* Building Industry Specific Fields */}
                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Client Name</label>
                                <input
                                    type="text"
                                    placeholder="Client Name"
                                    value={formData.client_name}
                                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Contract Number</label>
                                <input
                                    type="text"
                                    placeholder="Contract #"
                                    value={formData.contract_number}
                                    onChange={(e) => setFormData({...formData, contract_number: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Quotation ID</label>
                                <input
                                    type="text"
                                    placeholder="Quotation ID"
                                    value={formData.quotation_id}
                                    onChange={(e) => setFormData({...formData, quotation_id: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Document Links</label>
                                <input
                                    type="text"
                                    placeholder="URL to relevant documents"
                                    value={formData.document_links}
                                    onChange={(e) => setFormData({...formData, document_links: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    placeholder="Event Description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="mt-1 border rounded w-full p-2 h-20"
                                ></textarea>
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Additional Notes</label>
                                <textarea
                                    placeholder="Additional Notes"
                                    value={formData.additional_notes}
                                    onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                                    className="mt-1 border rounded w-full p-2 h-20"
                                ></textarea>
                            </div>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Attendees (comma
                                    separated)</label>
                                <input
                                    type="text"
                                    placeholder="email@example.com, another@example.com"
                                    value={formData.attendees}
                                    onChange={(e) => setFormData({...formData, attendees: e.target.value})}
                                    className="mt-1 border rounded w-full p-2"
                                />
                            </div>

                            {/* Date & Time Selection */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">Start</label>
                                <div className="flex gap-4 mt-1">
                                    <input
                                        type="date"
                                        value={formData.startDate}
                                        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                                        className="border rounded w-1/2 p-2"
                                    />
                                    <select
                                        value={formData.startTime}
                                        onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                                        className="border rounded w-1/2 p-2"
                                    >
                                        {generateTimeOptions().map((time) => (
                                            <option key={time} value={time}>
                                                {time}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">End</label>
                                <div className="flex gap-4 mt-1">
                                    <input
                                        type="date"
                                        value={formData.endDate}
                                        onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                                        className="border rounded w-1/2 p-2"
                                    />
                                    <select
                                        value={formData.endTime}
                                        onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                                        className="border rounded w-1/2 p-2"
                                    >
                                        {generateTimeOptions().map((time) => (
                                            <option key={time} value={time}>
                                                {time}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end mt-6">
                                <Button
                                    onClick={saveEvent}
                                    className="bg-primary text-white px-4 py-2 rounded hover:bg-green-600"
                                >
                                    {selectedEvent ? "Update Event" : "Save Event"}
                                </Button>
                                <DialogClose asChild>
                                    <Button
                                        className="ml-2 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">Cancel</Button>
                                </DialogClose>
                            </div>
                        </DialogContent>
                    </DialogPortal>
                </Dialog>
            )}
        </div>
    );
};

export default InternalCalendar;